import { LoadingState } from '@osteopro/ui';
import { lazy, Suspense } from 'react';
import { useLocation, useSearch } from 'wouter';
import { consultationRoute } from './consultation-tab-path.js';
import { usePatientTabs } from './patient-tabs.js';
import type { ConsultationTabInfo } from './patient-tabs-context.js';

const ConsultationForm = lazy(async () =>
  import('../../pages/consultations/ConsultationFormPage.js').then((module) => ({
    default: module.ConsultationForm,
  })),
);

/**
 * Garde chaque consultation ouverte montée. L'onglet inactif est masqué (`display: none`), il
 * n'est pas démonté : mesures, schéma et texte en cours restent dans le composant. La route, elle,
 * ne fait qu'inscrire l'onglet.
 */
export function ConsultationTabHost() {
  const { tabs } = usePatientTabs();
  const [location] = useLocation();
  const search = useSearch();
  const consultations = tabs.filter(
    (tab): tab is ConsultationTabInfo => tab.kind === 'consultation',
  );
  if (consultations.length === 0) return null;

  return (
    <>
      {consultations.map((tab) => (
        <ConsultationTabPanel key={tab.id} tab={tab} location={location} search={search} />
      ))}
    </>
  );
}

function ConsultationTabPanel({
  tab,
  location,
  search,
}: {
  tab: ConsultationTabInfo;
  location: string;
  search: string;
}) {
  const route = consultationRoute(location);
  const active = route?.id === tab.id;
  const section = active && route ? route.section : tab.section;
  const panelSearch = active ? search : tab.search;

  return (
    <div
      className={active ? 'flex min-w-0 flex-1 flex-col' : undefined}
      hidden={!active}
      style={active ? undefined : { display: 'none' }}
      data-active={active ? 'true' : 'false'}
      data-testid={`consultation-tab-panel-${tab.id}`}
    >
      <Suspense
        fallback={
          <div data-testid="state-loading">
            <LoadingState type="fiche" />
          </div>
        }
      >
        <ConsultationForm
          encounterId={tab.id}
          section={section}
          search={panelSearch}
          active={active}
        />
      </Suspense>
    </div>
  );
}
